import React, { useState } from 'react';
import { MessageSquare, RefreshCw, ThumbsUp, ThumbsDown, Send, AlertCircle } from 'lucide-react';
import { 
  analyzePostForKeywords, 
  generateCommentVariations, 
  moderateComment,
  analyzePostSentiment 
} from '../../../services/aiCommentGenerator';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AICommentPreview = ({ config }) => {
  const [testPost, setTestPost] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [sentimentResult, setSentimentResult] = useState(null);
  const [generatedComments, setGeneratedComments] = useState([]);
  const [selectedComment, setSelectedComment] = useState(null);
  const [moderationResult, setModerationResult] = useState(null);
  const [error, setError] = useState(null);

  const handleAnalyzePost = async () => {
    if (!testPost?.trim() || !config?.keywords?.length) {
      setError('Please enter test post content and ensure keywords are configured');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysisResult(null);
    setSentimentResult(null);
    setGeneratedComments([]);
    setSelectedComment(null);

    try {
      // Analyze keywords
      const keywordAnalysis = await analyzePostForKeywords(
        testPost,
        config?.keywords,
        config?.matchType
      );
      setAnalysisResult(keywordAnalysis);

      // Analyze sentiment
      const sentiment = await analyzePostSentiment(testPost);
      setSentimentResult(sentiment);

    } catch (err) {
      setError(err?.message || 'Failed to analyze post. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerateComments = async () => {
    if (!analysisResult?.isMatch) {
      setError('Post does not match keyword criteria');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const options = {
        postContent: testPost,
        commentStyle: config?.commentStyle,
        brandVoice: config?.brandVoice,
        keywords: analysisResult?.matchedKeywords,
        maxLength: 200
      };

      const comments = await generateCommentVariations(options, 3);
      setGeneratedComments(comments);
    } catch (err) {
      setError(err?.message || 'Failed to generate comments. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectComment = async (comment) => {
    setSelectedComment(comment);
    setModerationResult(null);

    // Automatically moderate selected comment
    try {
      const moderation = await moderateComment(comment);
      setModerationResult(moderation);
    } catch (err) {
      console.error('Moderation error:', err);
    }
  };

  const getSentimentColor = (sentiment) => {
    const colors = {
      positive: 'text-green-600 bg-green-50',
      negative: 'text-red-600 bg-red-50',
      neutral: 'text-gray-600 bg-gray-50',
      mixed: 'text-purple-600 bg-purple-50'
    };
    return colors?.[sentiment] || colors?.neutral;
  };

  const getEngagementColor = (level) => {
    const colors = {
      high: 'text-green-600 bg-green-50',
      medium: 'text-yellow-600 bg-yellow-50',
      low: 'text-gray-600 bg-gray-50'
    };
    return colors?.[level] || colors?.low;
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="w-5 h-5 text-green-600" />
        <h3 className="text-lg font-semibold text-gray-900">Test AI Comment Generation</h3>
      </div>
      {/* Test Post Input */}
      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter Test Post Content
          </label>
          <textarea
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="Paste a sample post to test AI comment generation..."
            value={testPost}
            onChange={(e) => setTestPost(e?.target?.value)}
          />
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleAnalyzePost}
            disabled={isAnalyzing || !testPost?.trim()}
            className="flex-1"
          >
            {isAnalyzing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              'Analyze Post'
            )}
          </Button>

          {analysisResult?.isMatch && (
            <Button
              onClick={handleGenerateComments}
              disabled={isGenerating}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Generate Comments
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      {/* Analysis Results */}
      {analysisResult && (
        <div className="mb-6 space-y-4">
          <div className={`p-4 rounded-lg border-2 ${
            analysisResult?.isMatch 
              ? 'bg-green-50 border-green-500' :'bg-gray-50 border-gray-300'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900">Keyword Analysis</h4>
              <span className={`text-xs font-medium px-2 py-1 rounded ${
                analysisResult?.isMatch ? 'bg-green-600 text-white' : 'bg-gray-600 text-white'
              }`}>
                {analysisResult?.isMatch ? 'MATCH' : 'NO MATCH'}
              </span>
            </div>
            <p className="text-sm text-gray-700 mb-2">{analysisResult?.reasoning}</p>
            {analysisResult?.matchedKeywords?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {analysisResult?.matchedKeywords?.map((keyword, idx) => (
                  <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                    {keyword}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2 text-xs text-gray-600">
              Relevance: {Math.round(analysisResult?.relevanceScore * 100)}%
            </div>
          </div>

          {sentimentResult && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
              <h4 className="text-sm font-semibold text-gray-900">Sentiment Analysis</h4>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-medium px-2 py-1 rounded ${getSentimentColor(sentimentResult?.sentiment)}`}>
                  {sentimentResult?.sentiment?.toUpperCase()}
                </span>
                <span className={`text-xs font-medium px-2 py-1 rounded ${getEngagementColor(sentimentResult?.engagementPotential)}`}>
                  {sentimentResult?.engagementPotential?.toUpperCase()} ENGAGEMENT
                </span>
                <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded">
                  {sentimentResult?.recommendedAction?.toUpperCase()}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-2">{sentimentResult?.reasoning}</p>
            </div>
          )}
        </div>
      )}
      {/* Generated Comments */}
      {generatedComments?.length > 0 && (
        <div className="space-y-3 mb-6">
          <h4 className="text-sm font-medium text-gray-700">Generated Comments:</h4>
          {generatedComments?.map((comment, index) => (
            <div
              key={index}
              className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                selectedComment === comment
                  ? 'border-green-500 bg-green-50' :'border-gray-200 hover:border-green-300'
              }`}
              onClick={() => handleSelectComment(comment)}
            >
              <p className="text-sm text-gray-800">{comment}</p>
              {selectedComment === comment && moderationResult && (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <div className="flex items-center gap-2">
                    {moderationResult?.isSafe ? (
                      <>
                        <ThumbsUp className="w-4 h-4 text-green-600" />
                        <span className="text-xs text-green-700 font-medium">Safe to post</span>
                      </>
                    ) : (
                      <>
                        <ThumbsDown className="w-4 h-4 text-red-600" />
                        <span className="text-xs text-red-700 font-medium">Flagged by moderation</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Use Selected Comment */}
      {selectedComment && moderationResult?.isSafe && (
        <Button className="w-full bg-green-600 hover:bg-green-700">
          <Send className="w-4 h-4 mr-2" />
          Use This Comment
        </Button>
      )}
    </div>
  );
};

export default AICommentPreview;